/*
  Quick add.

  Replaces Dawn's version wholesale, so every section that already loads
  quick-add.js and quick-add.css picks this up with no change of its own.

  Two paths from a card:
  - one variant, so there is nothing to choose: add it straight away
  - more than one: fetch quick-add-panel for that product and open it

  The panel is a section fetched by URL rather than a copy of the product
  page, so its markup is ours and it carries only the images and the choice.
*/

/*
  Several sections load this file, and a product page carries three of them
  at once. Same URL, so the browser runs it once, but a guard costs nothing
  and a second run would attach a second set of listeners: every click would
  then fire twice, which is what makes a close button need pressing twice.
*/
if (!window.quickAddLoaded) {
  window.quickAddLoaded = true;

  (() => {
    const PANEL_SECTION = 'quick-add-panel';

    // Must match the closing animation in quick-add.css. Kept as a number
    // here rather than read from the element, because a panel that fails to
    // animate would otherwise never be hidden at all.
    const CLOSE_MS = 300;

    // How long a pointer has to rest on a card before it counts as interest.
    // Long enough that sweeping across a row fetches nothing, short enough
    // that a deliberate move to a button still finishes before the click.
    const WARM_DELAY_MS = 120;

    let overlay = null;
    let panelHost = null;
    let lastTrigger = null;
    let closeTimer = null;
    let warmTimer = null;
    let variants = [];
    let mediaItems = [];
    let dots = [];

    // Panels already fetched, keyed by product URL. Opening the same card
    // twice, or hovering before clicking, then costs nothing.
    const cache = new Map();

    /* Cart -------------------------------------------------------------- */

    const cartTarget = () => document.querySelector('cart-drawer, cart-notification');

    const addToCart = async (variantId, button) => {
      const drawer = cartTarget();

      if (button) button.setAttribute('aria-busy', 'true');

      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', 1);

      if (drawer && drawer.getSectionsToRender) {
        body.append('sections', drawer.getSectionsToRender().map((section) => section.id));
        body.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body,
        });
        const result = await response.json();

        if (result.status) throw new Error(result.description || result.message);

        closePanel();
        if (drawer && drawer.renderContents) drawer.renderContents(result);
        else window.location.href = window.routes.cart_url;
      } catch (error) {
        // Falling back to the cart page is better than a button that appears
        // to do nothing.
        window.location.href = window.routes.cart_url;
      } finally {
        if (button) button.removeAttribute('aria-busy');
      }
    };

    /* Overlay ----------------------------------------------------------- */

    const onKeydown = (event) => {
      if (event.key === 'Escape') closePanel();
    };

    const build = () => {
      if (overlay) return;

      overlay = document.createElement('div');
      overlay.className = 'qa-overlay';
      overlay.hidden = true;
      overlay.innerHTML =
        '<div class="qa-overlay__panel" role="dialog" aria-modal="true">' +
        '<button type="button" class="qa-overlay__close" data-qa-close>' +
        '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
        '<path d="M1 1l14 14M15 1L1 15" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
        '</svg></button>' +
        '<div class="qa-overlay__body" data-qa-body></div>' +
        '</div>';

      document.body.appendChild(overlay);
      panelHost = overlay.querySelector('[data-qa-body]');

      const closeButton = overlay.querySelector('.qa-overlay__close');
      closeButton.setAttribute('aria-label', 'Close');

      overlay.addEventListener('click', (event) => {
        /*
          Anything outside the panel closes it. Testing for the panel rather
          than for a separate scrim element means there is no way to click a
          gap between the two and have nothing happen.
        */
        if (!event.target.closest('.qa-overlay__panel')) {
          closePanel();
          return;
        }

        if (event.target.closest('[data-qa-close]')) {
          closePanel();
          return;
        }

        const dot = event.target.closest('[data-qa-dot]');
        if (dot) {
          showMedia(Number(dot.dataset.qaDot));
          return;
        }

        // Clicking the gallery pages it, in the direction the cursor is
        // already pointing.
        const gallery = event.target.closest('[data-qa-media]');
        if (gallery) pageGallery(gallery, event.clientX);
      });

      overlay.addEventListener('change', (event) => {
        if (event.target.matches('[data-qa-option-position]')) onOptionChange();
      });

      overlay.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-qa-form]');
        if (!form) return;

        event.preventDefault();

        const input = form.querySelector('[data-qa-variant-id]');
        const button = form.querySelector('[data-qa-submit]');
        if (input && input.value) addToCart(input.value, button);
      });
    };

    const openPanel = () => {
      // A panel reopened mid close would otherwise inherit the closing
      // class and animate straight back out.
      clearTimeout(closeTimer);
      overlay.classList.remove('is-closing');

      overlay.hidden = false;
      document.body.classList.add('qa-open');
      document.addEventListener('keydown', onKeydown);
    };

    /*
      Closing runs the opening animation in reverse rather than cutting to
      nothing. The element stays in the document until it has finished, so
      the class drives the animation and a timer does the hiding.
    */
    function closePanel() {
      if (!overlay || overlay.hidden || overlay.classList.contains('is-closing')) return;

      document.removeEventListener('keydown', onKeydown);

      const finish = () => {
        overlay.hidden = true;
        overlay.classList.remove('is-closing');
        document.body.classList.remove('qa-open');

        // Focus goes back where it came from, so a keyboard user is not
        // dropped at the top of the page.
        if (lastTrigger) lastTrigger.focus();
      };

      // Nothing to reverse when there was nothing to play.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
        return;
      }

      overlay.classList.add('is-closing');
      closeTimer = setTimeout(finish, CLOSE_MS);
    }

    /* Gallery ----------------------------------------------------------- */

    /*
      The dashes are built from the images rather than rendered in Liquid,
      so the count cannot disagree with the gallery, and a product with one
      image gets no indicator at all rather than a single dash that does
      nothing.
    */
    const buildDots = () => {
      const list = overlay.querySelector('[data-qa-media]');
      const holder = overlay.querySelector('[data-qa-dots]');

      mediaItems = list ? Array.from(list.children) : [];
      dots = [];

      if (!holder) return;
      holder.innerHTML = '';

      if (mediaItems.length < 2) return;

      mediaItems.forEach((item, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'qa__dot';
        dot.dataset.qaDot = index;
        dot.setAttribute('aria-label', index + 1);
        if (index === 0) dot.setAttribute('aria-current', 'true');
        holder.appendChild(dot);
        dots.push(dot);
      });

      let ticking = false;
      list.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            syncDots(list);
            ticking = false;
          });
        },
        { passive: true }
      );
    };

    const syncDots = (list) => {
      const centre = list.scrollLeft + list.clientWidth / 2;

      let active = 0;
      let closest = Infinity;

      mediaItems.forEach((item, index) => {
        const distance = Math.abs(item.offsetLeft + item.offsetWidth / 2 - centre);
        if (distance < closest) {
          closest = distance;
          active = index;
        }
      });

      dots.forEach((dot, index) => dot.toggleAttribute('aria-current', index === active));
    };

    /*
      instant, not auto. The list carries scroll-behavior: smooth in CSS,
      and auto means "whatever the element says", so it would still animate.
      instant is the only value that overrides the stylesheet.
    */
    const showMedia = (index, jump) => {
      const list = overlay.querySelector('[data-qa-media]');
      const item = mediaItems[index];
      if (!list || !item) return;

      list.scrollTo({
        left: item.offsetLeft - list.offsetLeft,
        behavior: jump ? 'instant' : 'smooth',
      });
    };

    const showMediaById = (mediaId) => {
      const index = mediaItems.findIndex((item) => Number(item.dataset.mediaId) === mediaId);

      /*
        Instant, unlike a dot or a click on the image. Those are a request
        to move through the gallery, so the movement is the point. Choosing
        a colour is a request to see that colour, and sliding past every
        photo in between makes a choice feel like a journey.
      */
      if (index > -1) showMedia(index, true);
    };

    const pageGallery = (list, clientX) => {
      if (mediaItems.length < 2) return;

      const rect = list.getBoundingClientRect();
      const forward = clientX >= rect.left + rect.width / 2;

      list.scrollBy({ left: forward ? rect.width : -rect.width, behavior: 'smooth' });
    };

    /*
      The pointer becomes the control: a disc with an arrow pointing at the
      half of the image it is over. Arrows pinned to the edges would sit on
      top of the photograph the panel exists to show.

      The rotation is set as a plain number of degrees rather than swapping
      icons, so the browser interpolates 0 to 180 and always passes through
      90, where the arrow points down. That is what turns it under rather
      than over, in both directions.
    */
    const setupCursor = (panel) => {
      const list = panel.querySelector('[data-qa-media]');
      const media = panel.querySelector('.qa__media');
      if (!list || !media || mediaItems.length < 2) return;

      const cursor = document.createElement('div');
      cursor.className = 'qa__cursor';
      cursor.innerHTML =
        '<svg viewBox="0 0 16 12" fill="none" aria-hidden="true">' +
        '<path d="M1 6h14M10.5 1.5 15 6l-4.5 4.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>';
      media.appendChild(cursor);

      list.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'touch') return;

        const rect = media.getBoundingClientRect();
        cursor.classList.add('is-visible');
        cursor.style.transform =
          'translate(' +
          (event.clientX - rect.left - 22) +
          'px,' +
          (event.clientY - rect.top - 22) +
          'px)';

        const forward = event.clientX >= rect.left + rect.width / 2;
        cursor.style.setProperty('--qa-rotate', forward ? '0deg' : '180deg');
      });

      list.addEventListener('pointerleave', () => cursor.classList.remove('is-visible'));
    };

    /* Loading ----------------------------------------------------------- */

    const readVariants = (panel) => {
      const script = panel.querySelector('[data-qa-variants]');
      if (!script) return [];

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return [];
      }
    };

    const fetchPanel = (url) => {
      if (cache.has(url)) return cache.get(url);

      const target = new URL(url, window.location.origin);
      const separator = target.search ? '&' : '?';

      const request = fetch(
        target.pathname + target.search + separator + 'section_id=' + PANEL_SECTION
      ).then((response) => {
        if (!response.ok) throw new Error('Request failed');
        return response.text();
      });

      cache.set(url, request);
      return request;
    };

    const loadPanel = async (url, trigger) => {
      build();

      lastTrigger = trigger;
      panelHost.innerHTML = '<div class="qa__loading"></div>';
      openPanel();

      try {
        const markup = await fetchPanel(url);
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const panel = parsed.querySelector('[data-qa-panel]');
        if (!panel) throw new Error('No panel');

        panelHost.innerHTML = '';
        panelHost.appendChild(panel);

        variants = readVariants(panel);
        buildDots();
        setupCursor(panel);

        // The gallery is a fresh element every time the panel opens, so the
        // shared drag handler has to be told about it.
        if (window.attachDragScroll) {
          panel.querySelectorAll('[data-drag-scroll]').forEach(window.attachDragScroll);
        }
      } catch (error) {
        // A failed fetch should not be cached, or the card stays broken for
        // the rest of the visit.
        cache.delete(url);

        // The product page can do everything this panel can, so sending the
        // shopper there is a real fallback rather than an apology.
        window.location.href = url;
      }
    };

    /* Options ----------------------------------------------------------- */

    const onOptionChange = () => {
      const chosen = [];
      overlay.querySelectorAll('[data-qa-option-position]:checked').forEach((input) => {
        chosen[Number(input.dataset.qaOptionPosition) - 1] = input.value;
      });

      const variant = variants.find((item) =>
        item.options.every((value, index) => value === chosen[index])
      );

      overlay.querySelectorAll('[data-qa-option-value]').forEach((label) => {
        const value = chosen[Number(label.dataset.qaOptionValue) - 1];
        if (value) label.textContent = value;
      });

      /*
        Values that cannot be reached from the current selection, holding
        the other options fixed. Struck through rather than hidden, as on
        the product page: knowing a size exists but is gone is more useful
        than it silently not being there.
      */
      overlay.querySelectorAll('[data-qa-option-position]').forEach((input) => {
        const position = Number(input.dataset.qaOptionPosition) - 1;
        const candidate = chosen.slice();
        candidate[position] = input.value;

        const match = variants.find(
          (item) =>
            item.options.every((value, index) => index === position || value === candidate[index]) &&
            item.options[position] === input.value
        );

        const label = overlay.querySelector('label[for="' + input.id + '"]');
        if (label) label.classList.toggle('qa__value--unavailable', !(match && match.available));
      });

      const input = overlay.querySelector('[data-qa-variant-id]');
      const price = overlay.querySelector('[data-qa-price]');
      const compare = overlay.querySelector('[data-qa-compare]');
      const thumb = overlay.querySelector('[data-qa-thumb]');
      const submit = overlay.querySelector('[data-qa-submit]');
      const submitText = overlay.querySelector('[data-qa-submit-text]');

      if (input) input.value = variant ? variant.id : '';

      if (variant && price) {
        price.textContent = variant.price;
        price.classList.toggle('qa__price--sale', variant.on_sale);
      }

      if (variant && compare) {
        compare.textContent = variant.on_sale ? variant.compare_at_price : '';
        compare.hidden = !variant.on_sale;
      }

      if (submit) submit.disabled = !(variant && variant.available);

      if (submitText) {
        if (!variant) submitText.textContent = window.variantStrings.unavailable;
        else if (!variant.available) submitText.textContent = window.variantStrings.soldOut;
        else submitText.textContent = window.variantStrings.addToCart;
      }

      /*
        Both the desktop gallery and the mobile thumbnail follow the choice,
        and only when the variant has a photo of its own. Without that test,
        changing size on a product where only colour carries an image would
        blank the thumbnail and throw the gallery back to the first frame.

        Only one of the two is on screen at a time, so updating both is a
        line of work rather than a decision the script has to make about
        which breakpoint it is on.
      */
      if (variant && variant.image && thumb) thumb.src = variant.image;
      if (variant && variant.media_id) showMediaById(variant.media_id);
    };

    /* Triggers ---------------------------------------------------------- */

    /*
      Warmed on hover and on focus, after a short pause. By the time a click
      lands the markup is usually already in hand, which is the difference
      between a panel that appears and one that arrives.

      The pause is what keeps it honest. Without it, running the pointer
      across a row of five cards fetched five panels, none of which were
      wanted. Resting on one is intent; passing over it is not.
    */
    const warm = (event) => {
      const trigger = event.target.closest('[data-quick-add]');
      if (!trigger || trigger.dataset.variantId || !trigger.dataset.productUrl) return;

      // Already in hand, so there is nothing to wait for.
      if (cache.has(trigger.dataset.productUrl)) return;

      clearTimeout(warmTimer);
      warmTimer = setTimeout(() => {
        fetchPanel(trigger.dataset.productUrl).catch(() => {});
      }, WARM_DELAY_MS);
    };

    // Leaving cancels a pending fetch, so a pointer that pauses briefly on
    // its way somewhere else still costs nothing.
    const cancelWarm = (event) => {
      if (event.target.closest('[data-quick-add]')) clearTimeout(warmTimer);
    };

    document.addEventListener('pointerenter', warm, true);
    document.addEventListener('pointerleave', cancelWarm, true);
    document.addEventListener('focusin', warm);

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-quick-add]');
      if (!trigger) return;

      event.preventDefault();
      clearTimeout(warmTimer);

      // A single variant product has nothing to choose, so opening a panel
      // to press one button would be a step for its own sake.
      if (trigger.dataset.variantId) {
        lastTrigger = trigger;
        addToCart(trigger.dataset.variantId, trigger);
        return;
      }

      loadPanel(trigger.dataset.productUrl, trigger);
    });
  })();
}
